// Floating "+" fast-add button. Everyone can quickly request a shift; managers
// (supervisor+) also get a shortcut to open new times where people are needed.
import { useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '../auth'
import { api, ApiError } from '../api'
import { useI18n } from '../i18n'
import type { JobRole } from '../types'
import { todayYmd } from '../lib/dates'
import { Modal, Spinner, useToast } from './ui'
import { IconClock, IconPlus, IconSparkle } from './icons'
import QuickBook from './QuickBook'

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`

type Sheet = 'book' | 'open' | null

export default function QuickAdd() {
  const { user, hasRole } = useAuth()
  const { t } = useI18n()
  const [menuOpen, setMenuOpen] = useState(false)
  const [sheet, setSheet] = useState<Sheet>(null)
  const [roles, setRoles] = useState<JobRole[]>([])
  const isSupervisor = hasRole('supervisor')

  // Pull the job roles once so both quick forms can offer them.
  useEffect(() => {
    if (!user) return
    let active = true
    api
      .roles()
      .then((r) => active && setRoles(r.roles))
      .catch(() => {})
    return () => {
      active = false
    }
  }, [user])

  // Let Escape close the manager menu.
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  if (!user) return null

  // Members have a single action so the button books straight away; managers
  // get a little menu with the extra "open needs" shortcut.
  function onFab() {
    if (isSupervisor) setMenuOpen((v) => !v)
    else setSheet('book')
  }

  return (
    <>
      {menuOpen && <div className="fab-scrim" onClick={() => setMenuOpen(false)} />}
      <div className="fab-wrap">
        {isSupervisor && menuOpen && (
          <div className="fab-menu">
            <button
              type="button"
              className="fab-item"
              onClick={() => {
                setMenuOpen(false)
                setSheet('book')
              }}
            >
              <IconClock width={18} height={18} /> {t('topbar.bookShift')}
            </button>
            <button
              type="button"
              className="fab-item"
              onClick={() => {
                setMenuOpen(false)
                setSheet('open')
              }}
            >
              <IconSparkle width={18} height={18} /> {t('nav.openTimes')}
            </button>
          </div>
        )}
        <button
          type="button"
          className={`fab${menuOpen ? ' open' : ''}`}
          onClick={onFab}
          aria-label={t('fab.add')}
          title={t('fab.add')}
        >
          <IconPlus className="fab-ico" width={26} height={26} />
        </button>
        <div className="fab-version">v{__APP_VERSION__}</div>
      </div>

      {sheet === 'book' && <QuickBook roles={roles} onClose={() => setSheet(null)} />}
      {sheet === 'open' && isSupervisor && <QuickOpen roles={roles} onClose={() => setSheet(null)} />}
    </>
  )
}

// Open a window of hours that need people — same as the Open Times page, trimmed
// to the essentials for a quick add.
function QuickOpen({ roles, onClose }: { roles: JobRole[]; onClose: () => void }) {
  const { t } = useI18n()
  const toast = useToast()
  const [date, setDate] = useState(todayYmd())
  const [start, setStart] = useState(9)
  const [end, setEnd] = useState(17)
  const [required, setRequired] = useState(1)
  const [title, setTitle] = useState('')
  const [roleId, setRoleId] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (end <= start) {
      toast.push(t('ot.endAfterStart'), 'error')
      return
    }
    setBusy(true)
    try {
      await api.createDemand({
        date,
        start_hour: start,
        end_hour: end,
        required,
        role: '',
        notes: '',
        title: title.trim(),
        role_id: roleId || null,
      })
      toast.push(t('ot.toast.opened'), 'ok')
      onClose()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('ot.openErr'), 'error')
      setBusy(false)
    }
  }

  return (
    <Modal title={t('nav.openTimes')} subtitle={t('quick.open.sub')} onClose={onClose}>
      <form className="auth-form" onSubmit={submit}>
        <div className="field">
          <label>{t('ot.date')}</label>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>
        <div className="row gap-16">
          <div className="field grow">
            <label>{t('ot.from')}</label>
            <select className="input" value={start} onChange={(e) => setStart(Number(e.target.value))}>
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {hh(h)}
                </option>
              ))}
            </select>
          </div>
          <div className="field grow">
            <label>{t('ot.to')}</label>
            <select className="input" value={end} onChange={(e) => setEnd(Number(e.target.value))}>
              {HOURS.slice(1)
                .concat(24)
                .map((h) => (
                  <option key={h} value={h}>
                    {hh(h)}
                  </option>
                ))}
            </select>
          </div>
          <div className="field" style={{ width: 96 }}>
            <label>{t('ot.perHour')}</label>
            <input
              className="input"
              type="number"
              min={1}
              value={required}
              onChange={(e) => setRequired(Math.max(1, Number(e.target.value)))}
            />
          </div>
        </div>
        <div className="field">
          <label>
            {t('ot.eventName')} <span className="muted">({t('common.optional')})</span>
          </label>
          <input
            className="input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('ot.eventNamePlaceholder')}
          />
        </div>
        {roles.length > 0 && (
          <div className="field">
            <label>
              {t('roles.label')} <span className="muted">({t('common.optional')})</span>
            </label>
            <select className="input" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              <option value="">{t('roles.none')}</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? <Spinner /> : <><IconPlus width={18} height={18} /> {t('ot.openBtn')}</>}
          </button>
        </div>
      </form>
    </Modal>
  )
}
